import React, { useState, useEffect } from 'react';
import api from '../../config/axiosConfig';
import styles from './css/AdminTab.module.css';
import { ReactComponent as NoData } from '../../pages/img/NoData.svg';
import { Box, Card, CardContent, Divider, Grid, Link, TextField, Typography, Tooltip } from '@mui/material';
import WarningIcon from '@mui/icons-material/Warning';

const AdminTab = ({
  country,
  onOrderDataSend,
  year,
  month,
  filterSalesRep,
  filterCreatedDate
}) => {
  const [data, setData] = useState([]);
  const [editIndex, setEditIndex] = useState(null);
  const [editValues, setEditValues] = useState({});
  const [changedFields, setChangedFields] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // 沒有 country 就不抓
    if (!country) return;

    const fetchData = async () => {
      try {
        const lineApiPrefix = country === 'KR' ? '/korea/api' : '/japan/api';
        const dataResponse = await api.get(
          `${lineApiPrefix}/dashboard/?type=order_data_by_month_for_admin_detail&year=${year}&month=${month}`
        );
        setData(dataResponse.data);
        // 將當前 / 上月資料回傳給父層 DashBoard
        onOrderDataSend(dataResponse.data.current_month, dataResponse.data.last_month);
        setLoading(false);
      } catch (error) {
        console.error(error);
      }
    };
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [country, year, month]);

  const handleDoubleClick = (index, field, value) => {
    setEditIndex(index);
    setEditValues({ ...editValues, [field]: value });
  };

  const handleChange = (field, event) => {
    setEditValues((prevValues) => ({
      ...prevValues,
      [field]: event.target.value
    }));
    setChangedFields((prevFields) => ({
      ...prevFields,
      [field]: true
    }));
  };

  const handleBlur = () => {
    if (!data.current_month) return;
    const updatedOrders = [...data.current_month];
    let updates = {};

    Object.keys(editValues).forEach((key) => {
      if (changedFields[key] && editValues[key] !== updatedOrders[editIndex][key]) {
        updatedOrders[editIndex][key] = editValues[key];
        updates[key] = editValues[key];
      }
    });

    // 若有任何欄位被更新
    if (Object.keys(updates).length > 0) {
      // 依不同欄位呼叫對應的 patch API（以下假設仍是 japan/api，如韓國線需自行調整 patch 路徑）
      if ('orderfortable__fc_price' in updates) {
        api
          .patch(`/japan/api/OrderForTable/${updatedOrders[editIndex].orderfortable__id}/`, {
            fc_price: updates.orderfortable__fc_price
          })
          .catch((error) => console.error(error));
      }
      if ('orderfortable__fc_price_estimated' in updates) {
        api
          .patch(`/japan/api/OrderForTable/${updatedOrders[editIndex].orderfortable__id}/`, {
            fc_price_estimated: updates.orderfortable__fc_price_estimated
          })
          .catch((error) => console.error(error));
      }
      if ('charterbooking__booking_price' in updates) {
        api
          .patch(`/japan/api/CharterBooking/${updatedOrders[editIndex].charterbooking__id}/`, {
            booking_price: updates.charterbooking__booking_price
          })
          .catch((error) => console.error(error));
      }

      // 更新父層
      onOrderDataSend(updatedOrders, data.last_month);
    }

    setData({ ...data, current_month: updatedOrders });
    setEditIndex(null);
    setEditValues({});
    setChangedFields({});
  };

  const handleKeyDown = (event) => {
    if (event.key === 'Enter') {
      handleBlur();
    }
  };

  return (
    <Box className={styles['main-box']}>
      <Card className={styles['card']}>
        <CardContent className={styles['cardcontent']} sx={{ '&:last-child': { paddingBottom: 0 } }}>
          <Box className={styles['cardcontent-title-box']}>
            <Grid container spacing={1}>
              <Grid item xs={1.7}>
                <Typography className={styles['title-label']}>訂單團號</Typography>
              </Grid>
              <Grid item xs={1.7}>
                <Typography className={styles['title-label']}>外幣報價</Typography>
              </Grid>
              <Grid item xs={1.7}>
                <Typography className={styles['title-label']}>預估外幣報價</Typography>
              </Grid>
              <Grid item xs={1.7}>
                <Typography className={styles['title-label']}>外站報價</Typography>
              </Grid>
              <Grid item xs={1.7}>
                <Typography className={styles['title-label']}>代墊款</Typography>
              </Grid>
              <Grid item xs={1.7}>
                <Typography className={styles['title-label']}>獲利</Typography>
              </Grid>
              <Grid item xs={1.7}>
                <Typography className={styles['title-label']}>外站代收</Typography>
              </Grid>
            </Grid>
          </Box>

          <Box className={styles['cardcontent-content-box']}>
            <Grid container>
              {data.current_month &&
                data.current_month
                  .filter((order) => {
                    const bySales = filterSalesRep ? order.sales_rep === filterSalesRep : true;
                    const byDate = filterCreatedDate
                      ? new Date(order.created_at).toISOString().split('T')[0] === filterCreatedDate
                      : true;
                    return bySales && byDate;
                  })
                  .map((order, index) => (
                    <Grid
                      item
                      xs={12}
                      key={index}
                      sx={{ ':hover': { background: '#ebf1fd' } }}
                    >
                      <Grid container spacing={1} className={styles['content-grid']}>
                        {/* 團號 */}
                        <Grid item xs={1.7}>
                          <Typography className={styles['content-info']}>
                            <Link
                              href={`/${
                                country === 'KR' ? 'Korea' : 'Japan'
                              }/order-information/${order.uuid}`}
                              underline="none"
                              target="_blank"
                              rel="noopener"
                              className={styles['link-text']}
                            >
                              {order.group_number}
                            </Link>
                          </Typography>
                        </Grid>

                        {/* 外幣報價 (可編輯) */}
                        <Grid item xs={1.7}>
                          <Typography className={styles['content-info']}>
                            {editIndex === index && 'orderfortable__fc_price' in editValues ? (
                              <TextField
                                variant="standard"
                                value={editValues['orderfortable__fc_price'] || ''}
                                onChange={(e) => handleChange('orderfortable__fc_price', e)}
                                onBlur={handleBlur}
                                onKeyDown={handleKeyDown}
                                autoFocus
                                type="number"
                                inputProps={{
                                  style: {
                                    textAlign: 'left',
                                    padding: 0
                                  },
                                  disableUnderline: true
                                }}
                                sx={{
                                  '& .MuiInput-root': {
                                    width: '80px',
                                    fontSize: '15px'
                                  },
                                  '& input[type="number"]::-webkit-inner-spin-button, & input[type="number"]::-webkit-outer-spin-button':
                                    {
                                      '-webkit-appearance': 'none',
                                      margin: 0
                                    },
                                  '& input[type="number"]': {
                                    '-moz-appearance': 'textfield'
                                  }
                                }}
                              />
                            ) : (
                              <span
                                onDoubleClick={() =>
                                  handleDoubleClick(
                                    index,
                                    'orderfortable__fc_price',
                                    order.orderfortable__fc_price
                                  )
                                }
                              >
                                {order.orderfortable__fc_price ?? '–'}
                              </span>
                            )}
                          </Typography>
                        </Grid>

                        {/* 預估外幣報價 (可編輯) */}
                        <Grid item xs={1.7}>
                          <Typography className={styles['content-info']}>
                            {editIndex === index &&
                            'orderfortable__fc_price_estimated' in editValues ? (
                              <TextField
                                variant="standard"
                                value={editValues['orderfortable__fc_price_estimated'] || ''}
                                onChange={(e) =>
                                  handleChange('orderfortable__fc_price_estimated', e)
                                }
                                onBlur={handleBlur}
                                onKeyDown={handleKeyDown}
                                autoFocus
                                type="number"
                                inputProps={{
                                  style: {
                                    textAlign: 'left',
                                    padding: 0
                                  },
                                  disableUnderline: true
                                }}
                                sx={{
                                  '& .MuiInput-root': {
                                    width: '80px',
                                    fontSize: '15px'
                                  },
                                  '& input[type="number"]::-webkit-inner-spin-button, & input[type="number"]::-webkit-outer-spin-button':
                                    {
                                      '-webkit-appearance': 'none',
                                      margin: 0
                                    },
                                  '& input[type="number"]': {
                                    '-moz-appearance': 'textfield'
                                  }
                                }}
                              />
                            ) : (
                              <span
                                onDoubleClick={() =>
                                  handleDoubleClick(
                                    index,
                                    'orderfortable__fc_price_estimated',
                                    order.orderfortable__fc_price_estimated
                                  )
                                }
                              >
                                {order.orderfortable__fc_price_estimated ?? '–'}
                              </span>
                            )}
                          </Typography>
                        </Grid>

                        {/* 外站報價 (可編輯) */}
                        <Grid item xs={1.7}>
                          <Typography className={styles['content-info']}>
                            {editIndex === index &&
                            'charterbooking__booking_price' in editValues ? (
                              <TextField
                                variant="standard"
                                value={editValues['charterbooking__booking_price'] || ''}
                                onChange={(e) =>
                                  handleChange('charterbooking__booking_price', e)
                                }
                                onBlur={handleBlur}
                                onKeyDown={handleKeyDown}
                                autoFocus
                                type="number"
                                inputProps={{
                                  style: {
                                    textAlign: 'left',
                                    padding: 0
                                  },
                                  disableUnderline: true
                                }}
                                sx={{
                                  '& .MuiInput-root': {
                                    width: '80px',
                                    fontSize: '15px'
                                  },
                                  '& input[type="number"]::-webkit-inner-spin-button, & input[type="number"]::-webkit-outer-spin-button':
                                    {
                                      '-webkit-appearance': 'none',
                                      margin: 0
                                    },
                                  '& input[type="number"]': {
                                    '-moz-appearance': 'textfield'
                                  }
                                }}
                              />
                            ) : (
                              <span
                                onDoubleClick={() =>
                                  handleDoubleClick(
                                    index,
                                    'charterbooking__booking_price',
                                    order.charterbooking__booking_price
                                  )
                                }
                              >
                                {order.charterbooking__booking_price ?? '–'}
                              </span>
                            )}
                          </Typography>
                        </Grid>

                        {/* 代墊款 */}
                        <Grid item xs={1.7}>
                          <Typography className={styles['content-info']}>
                            {order.total_advance_amount ?? '–'}
                          </Typography>
                        </Grid>

                        {/* 獲利 (新增負值提示) */}
                        <Grid item xs={1.7}>
                          {(() => {
                            const profit = (order.orderfortable__fc_price || 0) -
                              (order.charterbooking__booking_price || 0) -
                              (order.total_advance_amount || 0);
                            const isNegative = profit < 0;
                            
                            return (
                              <Typography 
                                className={styles['content-info']} 
                                sx={{ 
                                  color: isNegative ? 'error.main' : 'inherit',
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: '4px'
                                }}
                              >
                                {profit || '–'}
                                {isNegative && (
                                  <Tooltip title="此訂單出現虧損" arrow>
                                    <WarningIcon 
                                      fontSize="small" 
                                      color="error" 
                                      sx={{ fontSize: '16px' }} 
                                    />
                                  </Tooltip>
                                )}
                              </Typography>
                            );
                          })()}
                        </Grid>

                        {/* 外站代收 */}
                        <Grid item xs={1.7}>
                          <Typography className={styles['content-info']}>
                            {order.orderfortable__jp_recive_price ?? '–'}
                          </Typography>
                        </Grid>
                      </Grid>
                      <Divider variant="middle" />
                    </Grid>
                  ))}
              {!loading && data.current_month && data.current_month.length === 0 && (
                <Grid item xs={12}>
                  <Box className={styles['no-data']}>
                    <NoData style={{ width: '300px', height: '300px' }} />
                    目前沒有任何訂單數據
                  </Box>
                </Grid>
              )}
            </Grid>
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
};

export default AdminTab;
